"use client";

/**
 * CollabV3FundingCard 컴포넌트
 * @description 상세의 모금 카드(앱 `CollabDetailFundingCard`) — 제목(+ 열림이 아니면 상태 배지),
 *   무엇에 쓰는지, `3,200 LS raised`, 진행 막대, 낸 사람 · 목표, 상태별 회색 안내, 본인 확인.
 *
 *   **보기 전용이다.** 모금 · 금액 변경 · 취소는 앱에서 하고, 그 길은 하단 앱 버튼 하나다.
 *   마감 날짜를 표시 시간대로 찍어야 해서 클라이언트 컴포넌트다.
 * @example <CollabV3FundingCard funding={detail.funding} isHostVerified />
 */
import { Card } from "@fanwork-web/design-system";
import { memo, useMemo } from "react";

import CollabV3AvatarStack from "./CollabV3AvatarStack";
import CollabV3Badge from "./CollabV3Badge";
import { collabV3CardStyles as styles } from "./CollabV3Card.styles";
import type { CollabV3FundingCardProps } from "./CollabV3Card.types";
import { collabV3DetailStyles } from "./CollabV3Detail.styles";
import CollabV3ProgressBar from "./CollabV3ProgressBar";
import { useCollabV3TimeZone } from "./CollabV3TimeZone";
import CollabV3VerifiedRow from "./CollabV3VerifiedRow";

import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import type { CollabV3Funding } from "../../entities";
import { formatCount, formatDay } from "../../utils/collabV3Format";

const copy = COLLAB_V3_CARD_COPY.funding;

/** 취소된 모금은 안내가 없다 — 배지가 말한다 */
function infoOf(funding: CollabV3Funding, date: string): string | null {
  switch (funding.status) {
    case "OPEN":
      return copy.info.open(date);
    case "SUCCEEDED":
      return `${copy.info.succeeded}${funding.isReported ? copy.info.reported : ""}`;
    case "FAILED":
      return copy.info.failed;
    default:
      return null;
  }
}

const CollabV3FundingCard = memo<CollabV3FundingCardProps>(
  ({ funding, isHostVerified }) => {
    const timeZone = useCollabV3TimeZone();
    const info = useMemo(
      () => infoOf(funding, formatDay(funding.endAt, timeZone)),
      [funding, timeZone],
    );
    const badge = funding.status === "OPEN" ? null : copy.badge[funding.status];

    return (
      <Card
        slot={
          <div className={styles.content}>
            <div className={styles.head}>
              <h3 className={styles.title}>{copy.title}</h3>
              {badge ? (
                <CollabV3Badge
                  text={badge}
                  tone={funding.status === "SUCCEEDED" ? "success" : "neutral"}
                />
              ) : null}
            </div>
            {funding.purpose ? (
              <div className={styles.stack}>
                <span className={styles.label}>{copy.purposeLabel}</span>
                <p className={styles.body}>{funding.purpose}</p>
              </div>
            ) : null}
            <div className={styles.stack}>
              <p className={styles.amountRow}>
                <span className={styles.amount}>
                  {COLLAB_V3_CARD_COPY.ls(formatCount(funding.collectedAmount))}
                </span>
                <span className={styles.unit}>{copy.collected}</span>
              </p>
              <CollabV3ProgressBar
                percent={funding.progressPercent}
                label={copy.progress}
                tone={funding.status === "FAILED" ? "muted" : "brand"}
              />
              <div className={styles.meta}>
                <span className={collabV3DetailStyles.participants.root}>
                  <CollabV3AvatarStack people={funding.supporters} />
                  {copy.supporters(funding.supporterCount)}
                </span>
                <span>{copy.goal(formatCount(funding.goalAmount))}</span>
              </div>
            </div>
            {info ? <p className={styles.info}>{info}</p> : null}
            {isHostVerified ? <CollabV3VerifiedRow /> : null}
          </div>
        }
      />
    );
  },
);

CollabV3FundingCard.displayName = "CollabV3FundingCard";

export default CollabV3FundingCard;
